import Base from '@components/layouts/base';
import ContentDescription from '@components/partials/common/contentDescription';
import Footer from '@components/partials/common/footer';
import MainTitle from '@components/partials/common/mainTitle';
import SubTitle from '@components/partials/common/subTitle';
import Topbar from '@components/partials/common/topbar';
import Header from '@components/partials/header/header';
import FinalThoughts from '@components/partials/navigation/finalThoughts';
import PaginationSite from '@components/partials/pagination/paginationSite';
import SiteCardRating from '@components/partials/site/cardRating';
import CategoryCloud from '@components/partials/site/categoryCloud';
import SiteList from '@components/partials/site/list';
import { getCategoryList } from '@data/category';
import { getCategoryReview } from '@data/categoryReview';
import { getIndexData } from '@data/index';
import { getLanguageList } from '@data/languageList';
import { getSiteReview } from '@data/siteReview';
import classes from '@styles/scss/common/_contentDescription.module.scss';
import { SiteSmallType } from 'lib/types/item/siteSmalltype';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';
import { useEffect, useState } from 'react';

const languageFiles = (require as any).context('public/locales', false, /\.json$/);

import redirect404 from 'lib/helper/404redirect';

import { IndexProps } from '../../lib/types/page/indexprops';

export default function Index({
    pageData,
    category,
    categoryList,
    categoryReviewData,
    site,
    siteReviewData,
    sitePagination,
    externalLink,
    languageList,
    currentLang
}: {
    pageData: IndexProps;
    category: any;
    categoryList: any;
    categoryReviewData: any;
    site: string;
    siteReviewData: any;
    sitePagination: SiteSmallType[];
    externalLink: string;
    languageList: any;
    currentLang: string;
}): JSX.Element {
    if (!pageData) return <div>Loading ...</div>;
    const cookies = parseCookies();
    const [updatedTourlink, setTourlink] = useState(externalLink);
    const languageFile = languageFiles(`./${currentLang}.json`);
    const title = languageFile.review.replace('%SITE_NAME%', siteReviewData.name);
    const router = useRouter();

    useEffect(() => {
        redirect404(router);
    }, []);

    useEffect(() => {
        if (cookies.u) {
            setTourlink(externalLink.replace('{exid}', cookies.u));
        }
    }, [cookies.u]);
    return (
        <>
            <Base>
                <Head>
                    <title>{siteReviewData.pageTitle}</title>
                    <meta name="description" content={siteReviewData.metaDescription} />
                    <meta name="robots" content="index, follow" />
                    <link
                        rel="canonical"
                        href={
                            process.env.NEXT_PUBLIC_APP_DOMAIN +
                            (currentLang != 'en' ? currentLang + '/' : '') +
                            category +
                            '/' +
                            site +
                            '/'
                        }
                    />
                    <link
                        rel="alternate"
                        href={process.env.NEXT_PUBLIC_APP_DOMAIN + category + '/' + site + '/'}
                        hrefLang="x-default"
                    />
                    {siteReviewData.pageLangs.map((lang: any) => {
                        const isReviewPublished = lang.isReviewPublished;
                        const langPath = lang.lang !== 'en' ? lang.lang + '/' : '';
                        const languageInfo = languageList.find(
                            (language: any) => language.slug === lang.lang
                        );
                        const hrefLang = languageInfo ? languageInfo.hreflang : lang.lang;

                        return isReviewPublished ? (
                            <link
                                key={lang.lang}
                                rel="alternate"
                                href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}${langPath}${category}/${site}/`}
                                hrefLang={hrefLang}
                            />
                        ) : null;
                    })}
                    <link
                        href="https://maxcdn.bootstrapcdn.com/font-awesome/4.6.3/css/font-awesome.min.css"
                        rel="stylesheet"
                        type="text/css"
                    />
                </Head>
                <div className="wrapper">
                    <Topbar
                        categoryList={categoryList}
                        homeSlug={'/' + currentLang}
                        languageList={siteReviewData.pageLangs}
                        isHomePage={false}
                    />
                    <Header intro={siteReviewData.intro} introEmail={siteReviewData.emailText} />
                    <MainTitle
                        title={siteReviewData.h1}
                        siteLink={updatedTourlink}
                        categoryName={categoryReviewData.categoryInfo.name}
                        linkText={siteReviewData.url}
                    />
                    <PaginationSite
                        siteName={siteReviewData.name}
                        siteSlug={site}
                        categoryName={categoryReviewData.categoryInfo.name}
                        categorySlug={category}
                        pagination={sitePagination}
                    />
                    <SiteCardRating
                        siteCardRating={siteReviewData.siteInfo}
                        siteLink={updatedTourlink}
                        categoryName={categoryReviewData.categoryInfo.name}
                        buttonText={languageFile.click_visit}
                    />
                    <div className={classes.contentDescription} data-pint-1321="setToNone">
                        <ContentDescription
                            siteName={siteReviewData.name}
                            siteLink={updatedTourlink}
                            categoryName={categoryReviewData.categoryInfo.name}
                            content={siteReviewData.content}
                            extraContent={siteReviewData.extraContent}
                            contentEmail={siteReviewData.emailText}
                            currentLang={currentLang}
                        />
                    </div>
                    <SubTitle siteName={siteReviewData.name} currentLang={currentLang} />
                    <SiteList categoryData={categoryReviewData} currentLang={currentLang} />
                    <CategoryCloud
                        categoryData={siteReviewData}
                        title={siteReviewData.cloudTitle}
                    />

                    <Footer />
                    <FinalThoughts content={siteReviewData.finalThoughts} />
                </div>
            </Base>
        </>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    const paths = [
        { params: { category: 'voyeur-cams', site: ['real-life-cam'] } },
        { params: { category: 'top-cam-sites', site: ['camtubechat'] } },
        { params: { category: 'top-cam-sites', site: ['rabbitscams'] } },
        { params: { category: 'voyeur-cams', site: ['camarads'] } },
        { params: { category: 'top-cam-sites', site: ['camrabbit'] } },
        { params: { category: 'live-cam-tubes', site: ['recurbate'] } }
    ];
    return {
        paths,
        fallback: 'blocking'
    };
};

// Get the data required to populate the page
export const getStaticProps: GetStaticProps = async (context: any) => {
    const category = context.params.category.toString();
    const siteParams = context.params.site;
    const site = siteParams[0].toString();
    const currentLang = context.locale;
    const locales = context.locales;
    if (category === 'out') {
        const siteReviewData = await getSiteReview(site, currentLang);
        return {
            redirect: {
                destination: siteReviewData.tourlink,
                permanent: false
            }
        };
    }

    const data = await getCategoryList(currentLang);
    const categoryList = data.categoryList;

    const sitePagination: SiteSmallType[] = [];

    const siteReviewData = await getSiteReview(site, currentLang);

    const languageList = getLanguageList();

    if (siteReviewData.categorySlug !== category) {
        return {
            notFound: true
        };
    }

    // check if data return is undefined and if 2nd param of site matches lang slug
    if (!siteReviewData) {
        return {
            notFound: true
        };
    } else if (siteParams.length > 1) {
        const langSlugs = languageList.map((lang: any) => lang.slug);
        if (!langSlugs.includes(siteParams[1].toString())) {
            return {
                notFound: true
            };
        }
    }

    // check if site has contents
    if (siteReviewData.isReviewPublished && categoryList[category]) {
        categoryList[category].activeSites.map((obj: any) => {
            if (obj.siteReviewActive) {
                const element = {
                    slug: obj.siteReviewSlug,
                    name: obj.siteName,
                    categorySlug: obj.siteCategorySlug
                };
                sitePagination.push(element);
            }
        });
    } else {
        return {
            notFound: true
        };
    }

    // temporary code to get opensite boolean

    let externalLink = siteReviewData.tourlink;
    const siteFromHome = categoryList[category].categorySites.filter(
        (obj: any) => obj.siteReviewSlug === site
    )[0];
    if (siteFromHome && siteFromHome.siteUseOpenSite) {
        externalLink = '/out/' + siteReviewData.slug;
    }

    const categoryReviewData = await getCategoryReview(category, currentLang);

    // Get the data from the respository to populate the page
    const pageData = await getIndexData();
    /*if (!pageData) {
        return {
            notFound: true
        };
    }*/
    return {
        props: {
            pageData,
            category,
            categoryList,
            categoryReviewData,
            site,
            siteReviewData,
            sitePagination,
            externalLink,
            languageList,
            currentLang,
            locales
        },
        revalidate: 300
    };
};
